import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, output } from '@angular/core';
import type { FormControl } from '@angular/forms';
import { ReactiveFormsModule } from '@angular/forms';

import type { CurrentUser, User } from '@core/account-types';
import type { Select2GroupWithItems } from '@core/user-interface-legacy';
import {
  Select2TriggerDirective,
  Select2Component,
  Select2ListItemDirective,
  OverlayPosition,
  HeaderDefaultComponent,
  GroupsFeatureDirective,
  Select2FooterDirective,
} from '@core/user-interface-legacy';
import { deepEqual } from '@core/utils-legacy';

import { MailboxSelectFooterComponent } from './mailbox-select-footer/mailbox-select-footer.component';
import { MailboxSelectItemComponent } from './mailbox-select-item/mailbox-select-item.component';
import { MailboxSelectTriggerButtonComponent } from './mailbox-select-trigger/mailbox-select-trigger.component';
import type { MailboxSelectItem } from './mailbox-select.types';
import { MailboxSelectType } from './mailbox-select.types';

@Component({
  selector: 'rt-mailbox-select',
  styleUrls: ['./mailbox-select.component.less'],
  template: `
    <ng-container
      *ngTemplateOutlet="groupedItems && groupedItems.length ? groupedItemsSelect : flatItemsSelect"
    />

    <ng-template #groupedItemsSelect>
      <rt-select2
        [groupedItems]="groupedItems"
        [formControl]="control"
        [overlayWidth]="350"
        [overlayPosition]="overlayPosition"
        [compareItemsFn]="compareItemsFn"
        [hiddenItems]="hiddenItems"
        [disabled]="disabled"
        (close)="selectClosed.emit()"
        (isOpen)="selectIsOpen.emit($event)"
        withGroups
      >
        @if (header) {
          <rt-select-header>{{ header }}</rt-select-header>
        }
        <rt-mailbox-select-trigger
          *rtSelectTrigger
          [disabled]="disabled"
          [currentUser]="currentUser"
          [minimized]="minimized"
          [triggerSize]="triggerSize"
        />
        <rt-mailbox-select-item
          *rtSelectListItem="
            itemType;
            let selectCtrl;
            let item = item;
            let isHoverState = isHoverState;
            let index = index
          "
          [highlighted]="isHoverState && !item.disabled"
          [users]="users"
          [currentUser]="currentUser"
          [item]="item"
          (click)="selectCtrl.select$.next(item)"
        />
      </rt-select2>
    </ng-template>

    <ng-template #flatItemsSelect>
      <rt-select2
        [items]="items"
        [formControl]="control"
        [disabled]="disabled"
        [overlayWidth]="350"
        [overlayPosition]="overlayPosition"
        [compareItemsFn]="compareItemsFn"
        [hiddenItems]="hiddenItems"
        (close)="selectClosed.emit()"
        (isOpen)="selectIsOpen.emit($event)"
      >
        @if (header) {
          <rt-select-header>{{ header }}</rt-select-header>
        }
        <rt-mailbox-select-trigger
          *rtSelectTrigger
          [currentUser]="currentUser"
          [minimized]="minimized"
          [triggerSize]="triggerSize"
        />
        <rt-mailbox-select-item
          *rtSelectListItem="
            itemType;
            let selectCtrl;
            let item = item;
            let isHoverState = isHoverState;
            let index = index
          "
          [highlighted]="isHoverState"
          [users]="users"
          [currentUser]="currentUser"
          [item]="item"
          (click)="selectCtrl.select$.next(item)"
        />

        <rt-mailbox-select-footer
          *rtSelectFooter
          [showManageCompanyButton]="showManageCompanyButton"
        />
      </rt-select2>
    </ng-template>
  `,
  imports: [
    Select2Component,
    MailboxSelectTriggerButtonComponent,
    MailboxSelectItemComponent,
    MailboxSelectFooterComponent,
    Select2TriggerDirective,
    Select2ListItemDirective,
    ReactiveFormsModule,
    HeaderDefaultComponent,
    GroupsFeatureDirective,
    NgTemplateOutlet,
    Select2FooterDirective,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MailboxSelectComponent {
  public readonly overlayPosition: OverlayPosition[] = [
    OverlayPosition.BottomLeft,
    OverlayPosition.TopLeft,
  ];

  public itemType: MailboxSelectItem;

  @Input({ required: true }) public control: FormControl;
  @Input({ required: true }) public currentUser: CurrentUser;
  @Input({ required: true }) public users: Record<number, User | null>;
  @Input() public triggerSize: 'small' | 'medium' = 'medium';
  @Input() public header: string;
  @Input() public items: MailboxSelectItem[] = [];
  @Input() public groupedItems: Array<Select2GroupWithItems<MailboxSelectItem>> = [];
  @Input() public minimized: boolean;
  @Input() public showManageCompanyButton: boolean = false;
  @Input() public disabled: boolean = false;

  public selectClosed = output<void>();
  public selectIsOpen = output<boolean>();

  public hiddenItems: MailboxSelectItem[];

  public ngOnInit() {
    if (
      this.control.value?.type === MailboxSelectType.AutomatedActionAdmin &&
      (!this.users[this.control.value.id] || this.users[this.control.value.id]?.anonymizedAt)
    ) {
      this.hiddenItems = [
        {
          label: 'app.common.deleted_user',
          value: this.control.value,
        },
      ];
    }
  }

  public compareItemsFn(a: MailboxSelectItem, b: MailboxSelectItem) {
    if (a.value && b.value) {
      if (a.value.type === b.value.type) {
        if ('id' in a.value) {
          if ('id' in b.value) {
            return a.value.id === b.value.id;
          } else {
            return false;
          }
        }

        return true;
      }
    }

    return deepEqual(a, b);
  }
}
